import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SortIcon, TableModule } from 'primeng/table';
import {
  MaestroConfig,
  MaestroFieldConfig,
  MaestroKey,
  MaestroOption,
} from '../../../../core/models/intranet-maestro';
import { AlertaService } from '../../../../core/services/alerta.service';
import { IntranetMantenedoresService } from '../../../../core/services/intranet-mantenedores.service';
import { LoadingService } from '../../../../core/services/loading.service';
import { StorageService } from '../../../../core/services/storage.service';

@Component({
  selector: 'app-tipo-carga-page',
  templateUrl: './tipo-carga.component.html',
  styleUrl: './tipo-carga.component.scss',
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule, InputTextModule, TableModule, SortIcon],
})
export class TipoCargaComponent implements OnInit {
  @Input() maestroKey: MaestroKey = 'tipo-carga';
  readonly configs: Record<MaestroKey, MaestroConfig> = {
    'tipo-carga': {
      key: 'tipo-carga',
      titulo: 'Tipo de carga',
      singular: 'tipo de carga',
      endpoint: 'tipo-carga',
      idKey: 'id',
      fields: [
        { key: 'descripcion', label: 'Descripción', type: 'text', required: true },
      ],
      columns: [
        { key: 'descripcion', label: 'Descripción' },
      ],
    },
    'tipo-combustion': {
      key: 'tipo-combustion',
      titulo: 'Tipo de combustión',
      singular: 'tipo de combustión',
      endpoint: 'tipo-combustion',
      idKey: 'id',
      fields: [
        { key: 'descripcion', label: 'Descripción', type: 'text', required: true },
      ],
      columns: [
        { key: 'descripcion', label: 'Descripción' },
      ],
    },
    'tipos-trabajo': {
      key: 'tipos-trabajo',
      titulo: 'Tipos de trabajo',
      singular: 'tipo de trabajo',
      endpoint: 'tipos-trabajo',
      idKey: 'id',
      fields: [
        { key: 'codigo', label: 'Código', type: 'text', required: true },
        { key: 'descripcion', label: 'Descripción', type: 'text', required: true },
      ],
      columns: [
        { key: 'codigo', label: 'Código' },
        { key: 'descripcion', label: 'Descripción' },
      ],
    },
    'tipos-vehiculo': {
      key: 'tipos-vehiculo',
      titulo: 'Tipos de vehículo',
      singular: 'tipo de vehículo',
      endpoint: 'tipos-vehiculo',
      idKey: 'id',
      fields: [
        { key: 'idFlota', label: 'Flota', type: 'select', required: true },
        { key: 'descripcion', label: 'Descripción', type: 'text', required: true },
        { key: 'precio', label: 'Precio', type: 'decimal' },
      ],
      columns: [
        { key: 'flota', label: 'Flota' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'precio', label: 'Precio', type: 'money' },
      ],
    },
    'tipos-traccion': {
      key: 'tipos-traccion',
      titulo: 'Tipos de tracción',
      singular: 'tipo de tracción',
      endpoint: 'tipos-traccion',
      idKey: 'id',
      fields: [
        { key: 'descripcion', label: 'Descripción', type: 'text', required: true },
      ],
      columns: [
        { key: 'descripcion', label: 'Descripción' },
      ],
    },
    flota: {
      key: 'flota',
      titulo: 'Flota',
      singular: 'flota',
      endpoint: 'flota',
      idKey: 'id',
      fields: [
        { key: 'descripcion', label: 'Descripción', type: 'text', required: true },
        { key: 'nivel', label: 'Nivel', type: 'number' },
        { key: 'tipoRegistro', label: 'Tipo de registro', type: 'number' },
      ],
      columns: [
        { key: 'descripcion', label: 'Descripción' },
        { key: 'nivel', label: 'Nivel' },
        { key: 'tipoRegistro', label: 'Tipo de registro' },
      ],
    },
  };

  config!: MaestroConfig;
  items: Record<string, any>[] = [];
  flotas: MaestroOption[] = [];
  visibleDialog = false;
  guardando = false;
  registro: Record<string, any> = {};

  constructor(
    private readonly mantenedoresService: IntranetMantenedoresService,
    private readonly storageService: StorageService,
    private readonly alertaService: AlertaService,
    private readonly loadingService: LoadingService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.config = this.configs[this.maestroKey];
    this.inicializarFormulario();
    this.cargarDatos();
  }

  get tituloDialogo(): string {
    const id = this.registro?.[this.config.idKey];
    return id ? `Editar ${this.config.singular}` : `Nuevo ${this.config.singular}`;
  }

  abrirNuevo(): void {
    this.inicializarFormulario();
    this.visibleDialog = true;
  }

  editar(item: Record<string, any>): void {
    this.registro = {
      ...item,
      activo: Number(item['activo'] ?? 1),
    };
    this.visibleDialog = true;
    this.cdr.detectChanges();
  }

  guardar(): void {
    const campoFaltante = this.config.fields.find(
      (field) =>
        field.required &&
        (this.registro[field.key] === null ||
          this.registro[field.key] === undefined ||
          this.registro[field.key] === ''),
    );

    if (campoFaltante) {
      this.alertaService.openAlerta(
        'Alerta',
        `El campo ${campoFaltante.label} es obligatorio`,
        'warn',
      );
      return;
    }

    this.guardando = true;
    this.loadingService.open('Guardando registro...');

    this.mantenedoresService.guardar(this.config.endpoint, this.buildPayload()).subscribe({
      next: (response) => {
        this.loadingService.close();
        this.guardando = false;

        if (!response.isSuccess) {
          this.alertaService.openAlerta('Alerta', response.message, 'error');
          return;
        }

        this.visibleDialog = false;
        this.alertaService.openAlerta('Correcto', response.message, 'success');
        this.cargarDatos();
      },
      error: (error) => {
        this.loadingService.close();
        this.guardando = false;
        this.alertaService.openAlerta('Error', error.message ?? 'No se pudo guardar', 'error');
      },
    });
  }

  confirmarEliminar(item: Record<string, any>): void {
    this.alertaService.openAlertaDesicion(
      'Eliminar registro',
      `¿Deseas eliminar ${item['descripcion'] ?? 'este registro'}?`,
      'warn',
      () => {
        void this.eliminar(item);
      },
    );
  }

  getValorCelda(item: Record<string, any>, key: string): string {
    const value = item[key];
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    return String(value);
  }

  formatearMoneda(value: unknown): string {
    return Number(value ?? 0).toFixed(2);
  }

  fieldOptions(field: MaestroFieldConfig): MaestroOption[] {
    if (field.key === 'idFlota') {
      return this.flotas;
    }
    return [];
  }

  trackByLabel(_index: number, item: any): string {
    return item.label;
  }

  private cargarDatos(): void {
    if (this.config.key === 'tipos-vehiculo') {
      this.cargarFlotas();
    }

    this.loadingService.open('Cargando registros...');
    this.mantenedoresService.listar(this.config.endpoint).subscribe({
      next: (response) => {
        this.loadingService.close();
        if (!response.isSuccess) {
          this.alertaService.openAlerta('Alerta', response.message, 'error');
          this.items = [];
          this.cdr.detectChanges();
          return;
        }
        this.items = response.value ?? [];
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.loadingService.close();
        this.items = [];
        this.cdr.detectChanges();
        this.alertaService.openAlerta('Error', error.message ?? 'No se pudo cargar', 'error');
      },
    });
  }

  private cargarFlotas(): void {
    this.mantenedoresService.listarFlotasActivas().subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.flotas = response.value ?? [];
        }
      },
    });
  }

  private inicializarFormulario(): void {
    this.registro = {
      activo: 1,
      nivel: 0,
      tipoRegistro: 0,
      precio: 0,
      idFlota: null,
    };
  }

  private async eliminar(item: Record<string, any>): Promise<void> {
    const id = Number(item[this.config.idKey]);
    return await new Promise((resolve) => {
      this.loadingService.open('Eliminando registro...');
      this.mantenedoresService.eliminar(this.config.endpoint, id).subscribe({
        next: (response) => {
          this.loadingService.close();
          if (response.isSuccess) {
            this.alertaService.openAlerta('Correcto', response.message, 'success');
            this.cargarDatos();
          } else {
            this.alertaService.openAlerta('Alerta', response.message, 'error');
          }
          resolve();
        },
        error: (error) => {
          this.loadingService.close();
          this.alertaService.openAlerta('Error', error.message ?? 'No se pudo eliminar', 'error');
          resolve();
        },
      });
    });
  }

  private buildPayload(): Record<string, unknown> {
    const usuario = this.storageService.storageGet<any>('usuario');
    const payload: Record<string, unknown> = {};

    for (const field of this.config.fields) {
      const value = this.registro[field.key];
      payload[field.key] = field.type === 'checkbox' ? (value ? 1 : 0) : value;
    }

    payload['activo'] = Number(this.registro['activo'] ?? 1);

    if (this.registro[this.config.idKey]) {
      payload['id'] = Number(this.registro[this.config.idKey]);
    } else if (usuario?.idIntranetUsuario) {
      payload['idUsuarioCreacion'] = Number(usuario.idIntranetUsuario);
    }

    return payload;
  }
}
